import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Paper, IconButton, Button, CircularProgress, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Chip, Box, InputAdornment } from '@mui/material';
import ActionConfirmModal from './ActionConfirmModal';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentsIcon from '@mui/icons-material/Payments';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrency, formatCurrency } from '../../../utils/currency';

export default function PurchaseOrderModals({
    // Create PO
    createPOModalOpen, setCreatePOModalOpen, isCreatingPO, poForm, setPoForm, suppliers, inventoryItems, 
    handleRemovePOLineItem, handlePOLineItemChange, handleAddPOLineItem, handleCreatePO,
    // Approve PO
    poApproveModalOpen, setPoApproveModalOpen, isReviewingPO, handleApprovePO,
    // Reject PO
    poRejectModalOpen, setPoRejectModalOpen, poRejectReason, setPoRejectReason, handleRejectPO,
    // Receive PO
    poReceiveModalOpen, setPoReceiveModalOpen, isReceivingPO, poReceiveForm, setPoReceiveForm, handleReceivePO,
    // Payment PO
    poPaymentModalOpen, setPoPaymentModalOpen, isRecordingPOPayment, poPaymentForm, setPoPaymentForm, handleRecordPOPayment, selectedPOForPayment,
    paymentMethods = [], loadingPaymentMethods = false
}) {
    const { currency, currencySymbol } = useCurrency();
    return (
        <>
            {/* Create Purchase Order Modal */}
            <Dialog 
                open={createPOModalOpen} 
                onClose={() => !isCreatingPO && setCreatePOModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Create Purchase Order</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" color="text.secondary" mb={2}>MASTER ORDER DETAILS</Typography>
                    <Grid container spacing={2} mb={4}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isCreatingPO}>
                                <InputLabel id="po-supplier-label" required>Supplier</InputLabel>
                                <Select
                                    labelId="po-supplier-label"
                                    label="Supplier"
                                    value={poForm.supplierId}
                                    onChange={(e) => setPoForm({ ...poForm, supplierId: e.target.value })}
                                >
                                    {suppliers.map(s => (
                                        <MenuItem key={s.id} value={s.id}>{s.companyName || s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                type="datetime-local"
                                label="Expected Delivery Date"
                                InputLabelProps={{ shrink: true }}
                                value={poForm.expectedDeliveryDate}
                                onChange={(e) => setPoForm({ ...poForm, expectedDeliveryDate: e.target.value })}
                                disabled={isCreatingPO}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Auditing Notes"
                                value={poForm.notes}
                                onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })}
                                disabled={isCreatingPO}
                                placeholder="E.g., Urgent guest linen replenishment"
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" color="text.secondary" mb={2}>LINE ITEMS</Typography>
                    {poForm.items.map((item, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, position: 'relative' }}>
                            {poForm.items.length > 1 && (
                                <IconButton 
                                    size="small" 
                                    color="error" 
                                    sx={{ position: 'absolute', top: 4, right: 4 }}
                                    onClick={() => handleRemovePOLineItem(index)}
                                    disabled={isCreatingPO}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required disabled={isCreatingPO} size="small">
                                        <InputLabel id={`po-item-label-${index}`} required>Inventory Item</InputLabel>
                                        <Select
                                            labelId={`po-item-label-${index}`}
                                            label="Inventory Item"
                                            value={item.itemId}
                                            onChange={(e) => handlePOLineItemChange(index, 'itemId', e.target.value)}
                                        >
                                            {inventoryItems.map(invItem => (
                                                <MenuItem key={invItem.id} value={invItem.id}>{invItem.name || invItem.sku || 'Unknown Item'}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        type="number"
                                        label="Quantity"
                                        placeholder="1"
                                        value={item.quantityOrdered}
                                        onChange={(e) => handlePOLineItemChange(index, 'quantityOrdered', e.target.value)}
                                        disabled={isCreatingPO}
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        type="number"
                                        label={`Unit Cost (${currencySymbol})`}
                                        placeholder="0.00"
                                        value={item.unitCost}
                                        onChange={(e) => handlePOLineItemChange(index, 'unitCost', e.target.value)}
                                        disabled={isCreatingPO}
                                        InputProps={{ inputProps: { min: 0.01, step: "0.01" } }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                    <Button 
                        startIcon={<AddIcon />} 
                        onClick={handleAddPOLineItem}
                        disabled={isCreatingPO}
                    >
                        Add Line Item Row
                    </Button>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setCreatePOModalOpen(false)} 
                        disabled={isCreatingPO}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreatePO} 
                        variant="contained" 
                        disabled={
                            isCreatingPO || 
                            !poForm.supplierId || 
                            !poForm.expectedDeliveryDate ||
                            poForm.items.length === 0 ||
                            poForm.items.some(item => !item.itemId || !item.quantityOrdered || Number(item.quantityOrdered) <= 0 || item.unitCost === '' || Number(item.unitCost) <= 0)
                        }
                        startIcon={isCreatingPO ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isCreatingPO ? 'Creating...' : 'Create Purchase Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Approve PO Modal */}
            <ActionConfirmModal
                open={poApproveModalOpen}
                onClose={() => setPoApproveModalOpen(false)}
                title="Approve Purchase Order"
                message="Are you sure you want to approve this purchase order? This action cannot be undone and will mark the order as authorized for fulfillment."
                onConfirm={handleApprovePO}
                isProcessing={isReviewingPO}
                confirmText="Approve Order"
                confirmColor="primary"
            />

            {/* Reject PO Modal */}
            <Dialog 
                open={poRejectModalOpen} 
                onClose={() => !isReviewingPO && setPoRejectModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject Purchase Order</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" mb={2}>
                        Please provide a reason for rejecting this purchase order. This will be recorded in the audit trail.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Rejection Reason"
                        value={poRejectReason}
                        onChange={(e) => setPoRejectReason(e.target.value)}
                        disabled={isReviewingPO}
                        required
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setPoRejectModalOpen(false)} 
                        disabled={isReviewingPO}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRejectPO} 
                        variant="contained" 
                        color="error"
                        disabled={isReviewingPO || !poRejectReason.trim()}
                        startIcon={isReviewingPO ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isReviewingPO ? 'Rejecting...' : 'Reject Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Receive PO Modal */}
            <Dialog 
                open={poReceiveModalOpen} 
                onClose={() => !isReceivingPO && setPoReceiveModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Log Shipment Intake</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Supplier Invoice / Reference Number"
                                value={poReceiveForm.supplierInvoiceNumber}
                                onChange={(e) => setPoReceiveForm({ ...poReceiveForm, supplierInvoiceNumber: e.target.value })}
                                disabled={isReceivingPO}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" mb={2}>EXPECTED LINE ITEMS</Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Expected Qty</TableCell>
                                            <TableCell align="right" sx={{ width: '150px' }}>Received Qty</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {poReceiveForm.items.map((item, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{item.itemName}</TableCell>
                                                <TableCell align="right">{item.quantityExpected}</TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={item.quantityReceived}
                                                        onChange={(e) => {
                                                            const newItems = [...poReceiveForm.items];
                                                            newItems[idx].quantityReceived = e.target.value;
                                                            setPoReceiveForm({ ...poReceiveForm, items: newItems });
                                                        }}
                                                        disabled={isReceivingPO}
                                                        InputProps={{ inputProps: { min: 0 } }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setPoReceiveModalOpen(false)} 
                        disabled={isReceivingPO}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleReceivePO} 
                        variant="contained" 
                        color="primary"
                        disabled={
                            isReceivingPO || 
                            !poReceiveForm.supplierInvoiceNumber?.trim() ||
                            poReceiveForm.items.some(item => Number(item.quantityReceived) < 0 || item.quantityReceived === '')
                        }
                        startIcon={isReceivingPO ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isReceivingPO ? 'Recording...' : 'Record Delivery Receipt'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Record PO Payment Modal */}
            <Dialog 
                open={poPaymentModalOpen} 
                onClose={() => !isRecordingPOPayment && setPoPaymentModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                {/* Modal Header */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    px: 3, 
                    py: 2.25, 
                    borderBottom: '1px solid #e2e8f0',
                    bgcolor: '#ffffff'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '10px', 
                            bgcolor: 'rgba(22, 163, 74, 0.1)', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: '#16a34a',
                            flexShrink: 0
                        }}>
                            <PaymentsIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a', lineHeight: 1.25 }}>
                                Record Purchase Order Payment
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                                Log vendor settlement and update accounting ledger
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        size="small" 
                        onClick={() => setPoPaymentModalOpen(false)} 
                        disabled={isRecordingPOPayment}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Modal Body */}
                <DialogContent sx={{ p: 3, bgcolor: '#ffffff' }}>
                    {selectedPOForPayment && (
                        <Box 
                            sx={{ 
                                p: 2, 
                                mb: 3, 
                                bgcolor: '#f8fafc', 
                                border: '1px solid #e2e8f0',
                                borderRadius: 2
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.25 }}>
                                        PO Reference
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        {selectedPOForPayment.poNumber || selectedPOForPayment.referenceCode || selectedPOForPayment.id}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.25 }}>
                                        Order Status
                                    </Typography>
                                    <Chip 
                                        label={selectedPOForPayment.status || 'Delivered'} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: '#dcfce7', 
                                            color: '#15803d', 
                                            fontWeight: 700, 
                                            fontSize: '0.75rem',
                                            height: 22,
                                            borderRadius: '6px'
                                        }} 
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.25 }}>
                                        Supplier / Vendor
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }} noWrap>
                                        {selectedPOForPayment.supplierName || (selectedPOForPayment.supplier ? selectedPOForPayment.supplier.companyName || selectedPOForPayment.supplier.name : 'N/A')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.25 }}>
                                        Total Order Value
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a', fontSize: '0.95rem' }}>
                                        {formatCurrency(selectedPOForPayment.totalAmount || selectedPOForPayment.totalCost || selectedPOForPayment.totalValue || selectedPOForPayment.orderValue || 0, currency)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Grid container spacing={2}>
                        {/* Row 1: Payment Method & Payment Amount */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required disabled={isRecordingPOPayment || loadingPaymentMethods}>
                                <InputLabel id="po-payment-method-label">Payment Method</InputLabel>
                                <Select
                                    labelId="po-payment-method-label"
                                    label="Payment Method"
                                    value={poPaymentForm.paymentMethod}
                                    onChange={(e) => setPoPaymentForm({ ...poPaymentForm, paymentMethod: e.target.value })}
                                    sx={{ borderRadius: 1.5 }}
                                >
                                    {loadingPaymentMethods ? (
                                        <MenuItem disabled value="">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="body2">Loading payment methods...</Typography>
                                            </Box>
                                        </MenuItem>
                                    ) : paymentMethods.length > 0 ? (
                                        paymentMethods.map((method, idx) => {
                                            const val = typeof method === 'string' ? method : (method.name || method.code || method.id);
                                            const label = typeof method === 'string' ? method : (method.label || method.name || method.code || val);
                                            return (
                                                <MenuItem key={method.id || method._id || val || idx} value={val}>
                                                    {label}
                                                </MenuItem>
                                            );
                                        })
                                    ) : (
                                        ['Bank Transfer', 'Cash', 'Credit Card', 'Debit Card', 'UPI', 'Cheque', 'Other'].map(method => (
                                            <MenuItem key={method} value={method}>{method}</MenuItem>
                                        ))
                                    )}
                                    {poPaymentForm.paymentMethod && !paymentMethods.some(m => (typeof m === 'string' ? m : (m.name || m.code || m.id)) === poPaymentForm.paymentMethod) && (
                                        <MenuItem key={poPaymentForm.paymentMethod} value={poPaymentForm.paymentMethod}>
                                            {poPaymentForm.paymentMethod}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="number"
                                label="Payment Amount"
                                value={poPaymentForm.amount}
                                onChange={(e) => setPoPaymentForm({ ...poPaymentForm, amount: e.target.value })}
                                disabled={isRecordingPOPayment}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                                    inputProps: { min: 0.01, step: "0.01" }
                                }}
                            />
                        </Grid>

                        {/* Row 2: Payment Reference & Category */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="Payment Reference / UTR"
                                placeholder="e.g. UTR-9876543210"
                                value={poPaymentForm.paymentReference}
                                onChange={(e) => setPoPaymentForm({ ...poPaymentForm, paymentReference: e.target.value })}
                                disabled={isRecordingPOPayment}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={isRecordingPOPayment}>
                                <InputLabel id="po-payment-category-label">Category</InputLabel>
                                <Select
                                    labelId="po-payment-category-label"
                                    label="Category"
                                    value={poPaymentForm.category}
                                    onChange={(e) => setPoPaymentForm({ ...poPaymentForm, category: e.target.value })}
                                    sx={{ borderRadius: 1.5 }}
                                >
                                    <MenuItem value="Inventory & Supplies">Inventory & Supplies</MenuItem>
                                    <MenuItem value="F&B">F&B</MenuItem>
                                    <MenuItem value="Room Supplies">Room Supplies</MenuItem>
                                    <MenuItem value="Housekeeping">Housekeeping</MenuItem>
                                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                                    <MenuItem value="General">General</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Row 3: Payment Date & Time */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="datetime-local"
                                label="Payment Date & Time"
                                InputLabelProps={{ shrink: true }}
                                value={poPaymentForm.paymentDate}
                                onChange={(e) => setPoPaymentForm({ ...poPaymentForm, paymentDate: e.target.value })}
                                disabled={isRecordingPOPayment}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                            />
                        </Grid>

                        {/* Row 4: Notes */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                label="Payment Notes"
                                placeholder="e.g. Paid Amber Clean & Hygiene"
                                value={poPaymentForm.notes}
                                onChange={(e) => setPoPaymentForm({ ...poPaymentForm, notes: e.target.value })}
                                disabled={isRecordingPOPayment}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Modal Footer Actions */}
                <DialogActions sx={{ 
                    px: 3, 
                    py: 2, 
                    bgcolor: '#f8fafc', 
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 1.5 
                }}>
                    <Button 
                        onClick={() => setPoPaymentModalOpen(false)} 
                        disabled={isRecordingPOPayment}
                        variant="outlined"
                        sx={{
                            color: '#64748b',
                            borderColor: '#cbd5e1',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            px: 2.5,
                            py: 0.8,
                            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRecordPOPayment} 
                        variant="contained" 
                        disabled={
                            isRecordingPOPayment || 
                            !poPaymentForm.paymentMethod || 
                            !poPaymentForm.amount || 
                            Number(poPaymentForm.amount) <= 0 ||
                            !poPaymentForm.paymentReference?.trim()
                        }
                        startIcon={isRecordingPOPayment ? <CircularProgress size={18} color="inherit" /> : <PaymentsIcon sx={{ fontSize: 19 }} />}
                        sx={{
                            bgcolor: '#16a34a',
                            color: '#ffffff',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            px: 3,
                            py: 0.85,
                            boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)',
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': {
                                bgcolor: '#15803d',
                                boxShadow: '0 6px 16px rgba(22, 163, 74, 0.35)',
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#e2e8f0',
                                color: '#94a3b8'
                            }
                        }}
                    >
                        {isRecordingPOPayment ? 'Recording...' : 'Record Payment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
